import { getToolName, isToolUIPart } from "ai";
import type { AgentUIMessage } from "@/lib/ai/types";

/** 最新 assistant message に未応答の承認が残っているか判定する。 */
export function hasPendingToolApproval(messages: AgentUIMessage[]): boolean {
  const latestMessage = messages.at(-1);
  return (
    latestMessage?.role === "assistant" &&
    latestMessage.parts.some(
      (part) => isToolUIPart(part) && part.state === "approval-requested",
    )
  );
}

/**
 * 保存済みの承認応答から model continuation を開始できるか判定する。
 *
 * @remarks
 * Tool を含む最新 step の native approval、legacy confirmation、通常 Tool がすべて
 * 終端状態になるまで継続しない。末尾の空 step は無視する。server が step limit を
 * 確定した message は新しい request を連鎖させず、その終了理由を維持する。
 */
export function isApprovalContinuationReady(
  messages: AgentUIMessage[],
  legacyConfirmationToolNames: ReadonlySet<string>,
): boolean {
  const latestMessage = messages.at(-1);
  if (latestMessage?.role !== "assistant") {
    return false;
  }
  if (latestMessage.metadata?.completionCause === "step-limit") {
    return false;
  }

  let stepEndIndex = latestMessage.parts.length;
  let lastStepToolParts = latestMessage.parts.filter(isToolUIPart);
  while (stepEndIndex >= 0) {
    let stepStartIndex = -1;
    for (let index = stepEndIndex - 1; index >= 0; index -= 1) {
      if (latestMessage.parts[index]?.type === "step-start") {
        stepStartIndex = index;
        break;
      }
    }

    const stepParts = latestMessage.parts.slice(
      stepStartIndex + 1,
      stepEndIndex,
    );
    if (stepParts.length > 0 || stepStartIndex === -1) {
      lastStepToolParts = stepParts.filter(isToolUIPart);
      break;
    }
    stepEndIndex = stepStartIndex;
  }
  const hasCompletedConfirmation = lastStepToolParts.some(
    (part) =>
      part.state === "approval-responded" ||
      (legacyConfirmationToolNames.has(getToolName(part)) &&
        (part.state === "output-available" || part.state === "output-error")),
  );

  return (
    hasCompletedConfirmation &&
    lastStepToolParts.every(
      (part) =>
        part.state === "output-available" ||
        part.state === "output-error" ||
        part.state === "output-denied" ||
        part.state === "approval-responded",
    )
  );
}
